/* =========================================================
   Cierre de sesión por inactividad.

   Vitalino lo probó (13.5): dejó la sesión abierta una hora y seguía
   dentro. Y lo estaba: el token de refresco vive catorce días y se
   renueva solo cuando hay actividad, así que una pestaña olvidada en un
   equipo compartido quedaba abierta durante días.

   Aquí se mide el tiempo con una marca de reloj, no con un setTimeout
   por evento: si el portátil se suspende, el temporizador no dispara
   cuando debería, pero la resta de fechas sí lo nota al despertar.

   Dos minutos antes se avisa. Cualquier señal de vida —una tecla, el
   ratón, un dedo en la pantalla— cancela la cuenta atrás y retira el
   aviso; nadie debería perder un formulario a medio llenar sin verlo
   venir.
   ========================================================= */
import { useEffect, useRef } from 'react';
import { useAppStore } from '@/store/useAppStore';

/** Minutos sin actividad tras los que se cierra la sesión. */
export const INACTIVIDAD_MIN = 60;
/** Minutos de aviso previo. */
const AVISO_MIN = 2;
/** Cada cuánto se comprueba el reloj. */
const LATIDO_MS = 15_000;

const EVENTOS = [
  'pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll',
] as const;

/** Cierra la sesión cuando pasa demasiado tiempo sin que nadie toque nada.
 *
 *  `cerrar` recibe el motivo para que la pantalla de acceso lo pueda
 *  explicar en vez de aparecer sin más. */
export function useInactividad(activo: boolean, cerrar: () => void) {
  const addToast = useAppStore((s) => s.addToast);
  const dismissToast = useAppStore((s) => s.dismissToast);
  const lang = useAppStore((s) => s.lang);

  // En refs: cambiar de idioma o volver a renderizar no debe reiniciar la
  // cuenta atrás ni volver a suscribir los eventos.
  const ultimo = useRef(Date.now());
  const avisoId = useRef<string | null>(null);
  const cerrarRef = useRef(cerrar);
  const langRef = useRef(lang);
  cerrarRef.current = cerrar;
  langRef.current = lang;

  useEffect(() => {
    if (!activo) return;

    ultimo.current = Date.now();

    const vida = () => {
      ultimo.current = Date.now();
      if (avisoId.current) {
        dismissToast(avisoId.current);
        avisoId.current = null;
      }
    };

    for (const ev of EVENTOS) {
      window.addEventListener(ev, vida, { passive: true, capture: true });
    }

    const reloj = window.setInterval(() => {
      const quieto = (Date.now() - ultimo.current) / 60_000;
      const es = langRef.current === 'es';

      if (quieto >= INACTIVIDAD_MIN) {
        window.clearInterval(reloj);
        if (avisoId.current) dismissToast(avisoId.current);
        avisoId.current = null;
        cerrarRef.current();
        return;
      }

      if (quieto >= INACTIVIDAD_MIN - AVISO_MIN && !avisoId.current) {
        avisoId.current = addToast({
          kind: 'warn',
          persistente: true,
          title: es ? 'La sesión está a punto de cerrarse' : 'Your session is about to end',
          body: es
            ? `Llevas ${INACTIVIDAD_MIN - AVISO_MIN} minutos sin actividad. `
              + `En ${AVISO_MIN} minutos se cerrará sola. Mueve el ratón o pulsa una tecla para seguir.`
            : `You have been inactive for ${INACTIVIDAD_MIN - AVISO_MIN} minutes. `
              + `It will close in ${AVISO_MIN} minutes. Move the mouse or press a key to stay.`,
          details: es
            ? 'Si tienes un formulario a medio llenar, guárdalo ahora: al cerrarse la sesión se pierde lo escrito.'
            : 'If you have a half-filled form, save it now: what you typed is lost when the session closes.',
        });
      }
    }, LATIDO_MS);

    return () => {
      window.clearInterval(reloj);
      for (const ev of EVENTOS) {
        window.removeEventListener(ev, vida, { capture: true });
      }
      if (avisoId.current) {
        dismissToast(avisoId.current);
        avisoId.current = null;
      }
    };
  }, [activo, addToast, dismissToast]);
}
